import { Link } from "react-router-dom";
import { Shield, Users, MapPin, Clock, CheckCircle, Award } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const About = () => {
  return (
    <>
      <SEOHead 
        title="About Us | uplino.in - India's Premium Classified Ads Platform"
        description="Learn about uplino.in - India's leading classified ads platform connecting service providers with customers across 270+ cities. Trusted by thousands of advertisers."
        keywords={["about uplino", "classified ads india", "escort service directory", "massage service platform"]}
        canonical="https://uplino.in/about"
      />
      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-950 to-black">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-rose-900/20 via-transparent to-transparent"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                About <span className="text-rose-400">uplino.in</span>
              </h1>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                India's premier classified ads platform connecting premium service providers 
                with customers across 270+ cities. We are committed to providing a safe, 
                reliable, and efficient platform for adult entertainment services.
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-8">
                <div className="w-14 h-14 bg-rose-500/20 rounded-lg flex items-center justify-center mb-6">
                  <Award className="w-7 h-7 text-rose-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
                <p className="text-gray-400 leading-relaxed">
                  To create India's most trusted and efficient classified ads platform 
                  that empowers service providers to reach their target audience while 
                  ensuring user safety and privacy. We strive to maintain the highest 
                  standards of quality and professionalism in the industry.
                </p>
              </div>

              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-8">
                <div className="w-14 h-14 bg-amber-500/20 rounded-lg flex items-center justify-center mb-6">
                  <Shield className="w-7 h-7 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
                <p className="text-gray-400 leading-relaxed">
                  To become the go-to platform for premium classified services in India, 
                  known for our commitment to user verification, content authenticity, 
                  and exceptional customer support. We aim to set new benchmarks in the 
                  classified ads industry.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Stats */}
        <section className="py-16 bg-slate-900/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Why Choose uplino.in?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-slate-900/50 border border-white/10 rounded-xl">
                <div className="text-4xl font-bold text-rose-400 mb-2">270+</div>
                <div className="text-gray-400 text-sm">Cities Covered</div>
              </div>
              <div className="text-center p-6 bg-slate-900/50 border border-white/10 rounded-xl">
                <div className="text-4xl font-bold text-amber-400 mb-2">10K+</div>
                <div className="text-gray-400 text-sm">Active Advertisers</div>
              </div>
              <div className="text-center p-6 bg-slate-900/50 border border-white/10 rounded-xl">
                <div className="text-4xl font-bold text-emerald-400 mb-2">99.9%</div>
                <div className="text-gray-400 text-sm">Uptime Guaranteed</div>
              </div>
              <div className="text-center p-6 bg-slate-900/50 border border-white/10 rounded-xl">
                <div className="text-4xl font-bold text-blue-400 mb-2">24/7</div>
                <div className="text-gray-400 text-sm">Customer Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              Platform Features
            </h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              We've built a comprehensive platform with features designed to help 
              advertisers succeed and customers find what they're looking for.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rose-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500/30 transition-colors">
                    <MapPin className="w-6 h-6 text-rose-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Multi-City Reach</h3>
                    <p className="text-gray-400 text-sm">
                      Post your ads across 270+ cities in India with a single account. 
                      Maximize your visibility and reach potential customers everywhere.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/30 transition-colors">
                    <Clock className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Time Slot Targeting</h3>
                    <p className="text-gray-400 text-sm">
                      Choose specific time slots to display your ads. Target customers 
                      during peak hours and optimize your advertising spend.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/30 transition-colors">
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Verified Advertisers</h3>
                    <p className="text-gray-400 text-sm">
                      Our verification process ensures authentic listings. Build trust 
                      with customers through our badge system.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <Shield className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Privacy Protection</h3>
                    <p className="text-gray-400 text-sm">
                      Your personal information is safe with us. We use advanced 
                      encryption and never share your data with third parties.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/30 transition-colors">
                    <CheckCircle className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Instant Approval</h3>
                    <p className="text-gray-400 text-sm">
                      Get your ads live instantly upon wallet payment. No waiting 
                      for manual approvals - start getting leads immediately.
                    </p>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-pink-500/30 transition-colors">
                    <Award className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Boost & Pin Ads</h3>
                    <p className="text-gray-400 text-sm">
                      Bump your ads to the top or get featured placement. 
                      Stand out from the competition and get more visibility.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-rose-900/30 to-amber-900/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of advertisers who trust uplino.in to grow their business. 
              Register now and get 2 free ad posts to try our platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg transition-colors"
              >
                Create Free Account
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg border border-white/20 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default About;
